import React, { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import {
  getSettlementOverview,
  getDriverBalances,
  listSettlements,
  getSettlementDetail,
  updateSettlementStatus,
  generateSettlements,
  getGstReport,
  downloadSettlementsCsv,
} from "../api/driverSettlementApi";

// ═══ Same brand green as PassengerWalletPage ═══
const BRAND = {
  primary: "#419952",
  primaryDark: "#357A43",
  primaryBg: "#EAF5EC",
  primaryBorder: "#B4D9BB",
};

const STATUS_STYLE = {
  PENDING: { bg: "#FFF4DB", fg: "#8A5A00", label: "Pending" },
  PROCESSING: { bg: "#E3EEFF", fg: "#1D4FB8", label: "Processing" },
  ON_HOLD: { bg: "#ECECEC", fg: "#555", label: "On hold" },
  FAILED: { bg: "#FDE4E4", fg: "#B42318", label: "Failed" },
  PAID: { bg: BRAND.primaryBg, fg: BRAND.primaryDark, label: "Paid" },
};

const inr = (n) =>
  "₹" +
  Number(n || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-IN", {
        timeZone: "Asia/Kolkata",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    : "—";

const toISO = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

// Previous week's Monday (local time — admin India-la irukkaanga nu assume)
const previousMondayISO = () => {
  const d = new Date();
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7) - 7);
  return toISO(d);
};
const monthStartISO = () => {
  const d = new Date();
  return toISO(new Date(d.getFullYear(), d.getMonth(), 1));
};

// ───────────────────────── small UI pieces ─────────────────────────
const S = {
  card: {
    background: "#fff",
    border: "1px solid #E5E7EB",
    borderRadius: 12,
    padding: 16,
  },
  th: {
    textAlign: "left",
    padding: "10px 12px",
    fontSize: 12,
    color: "#6B7280",
    fontWeight: 600,
    whiteSpace: "nowrap",
    borderBottom: "1px solid #E5E7EB",
    background: "#F9FAFB",
  },
  td: {
    padding: "10px 12px",
    fontSize: 13,
    borderBottom: "1px solid #F1F3F5",
    whiteSpace: "nowrap",
  },
  input: {
    padding: "8px 10px",
    border: "1px solid #D1D5DB",
    borderRadius: 8,
    fontSize: 13,
    outline: "none",
  },
  btn: {
    padding: "8px 14px",
    borderRadius: 8,
    border: "none",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    background: BRAND.primary,
    color: "#fff",
  },
  btnGhost: {
    padding: "8px 14px",
    borderRadius: 8,
    border: `1px solid ${BRAND.primaryBorder}`,
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    background: "#fff",
    color: BRAND.primaryDark,
  },
};

function StatCard({ label, value, hint, accent }) {
  return (
    <div
      style={{
        ...S.card,
        borderTop: `3px solid ${accent || BRAND.primary}`,
        flex: "1 1 190px",
        minWidth: 190,
      }}
    >
      <div style={{ fontSize: 12, color: "#6B7280", marginBottom: 6 }}>
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, color: "#111827" }}>
        {value}
      </div>
      {hint && (
        <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 4 }}>
          {hint}
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.PENDING;
  return (
    <span
      style={{
        background: s.bg,
        color: s.fg,
        padding: "3px 10px",
        borderRadius: 20,
        fontSize: 12,
        fontWeight: 600,
      }}
    >
      {s.label}
    </span>
  );
}

function Modal({ title, onClose, children, width = 460 }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,.45)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff",
          borderRadius: 14,
          width,
          maxWidth: "100%",
          maxHeight: "90vh",
          overflowY: "auto",
          padding: 20,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 14,
          }}
        >
          <h3 style={{ margin: 0, fontSize: 17 }}>{title}</h3>
          <button
            onClick={onClose}
            style={{
              border: "none",
              background: "none",
              fontSize: 22,
              cursor: "pointer",
              color: "#6B7280",
            }}
          >
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Pager({ page, totalPages, onChange, disabled }) {
  if (!totalPages || totalPages <= 1) return null;
  return (
    <div
      style={{
        display: "flex",
        gap: 10,
        alignItems: "center",
        justifyContent: "flex-end",
        marginTop: 12,
      }}
    >
      <button
        style={S.btnGhost}
        disabled={disabled || page <= 1}
        onClick={() => onChange(page - 1)}
      >
        ← Prev
      </button>
      <span style={{ fontSize: 13, color: "#6B7280" }}>
        Page {page} / {totalPages}
      </span>
      <button
        style={S.btnGhost}
        disabled={disabled || page >= totalPages}
        onClick={() => onChange(page + 1)}
      >
        Next →
      </button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
export default function DriverSettlementsPage() {
  const [tab, setTab] = useState("settlements");
  const [overview, setOverview] = useState(null);

  // ── Settlements tab ──
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [weekStart, setWeekStart] = useState("");
  const [status, setStatus] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const listReq = useRef(0);

  // ── Drivers tab ──
  const [drvRows, setDrvRows] = useState([]);
  const [drvPage, setDrvPage] = useState(1);
  const [drvPages, setDrvPages] = useState(1);
  const [drvLoading, setDrvLoading] = useState(false);
  const [drvSearchInput, setDrvSearchInput] = useState("");
  const [drvSearch, setDrvSearch] = useState("");

  // ── GST tab ──
  const [gstFrom, setGstFrom] = useState(monthStartISO());
  const [gstTo, setGstTo] = useState(toISO(new Date()));
  const [gst, setGst] = useState(null);
  const [gstLoading, setGstLoading] = useState(false);

  // ── Modals ──
  const [payTarget, setPayTarget] = useState(null);
  const [payForm, setPayForm] = useState({
    paymentMode: "UPI",
    utr: "",
    remarks: "",
  });
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null); // { settlement, earnings }
  const [detailLoading, setDetailLoading] = useState(false);
  const [showGenerate, setShowGenerate] = useState(false);
  const [genWeek, setGenWeek] = useState(previousMondayISO());
  const [generating, setGenerating] = useState(false);

  // ── Loaders ────────────────────────────────────────────────────────────────
  const loadOverview = useCallback(async () => {
    const d = await getSettlementOverview();
    if (d.success) setOverview(d);
    else toast.error(d.error || "Could not load overview");
  }, []);

  const loadSettlements = useCallback(async (p, filters) => {
    const id = ++listReq.current;
    setLoading(true);
    const d = await listSettlements({ page: p, limit: 10, ...filters });
    if (id !== listReq.current) return;
    if (d.success) {
      setRows(d.settlements);
      setTotals(d.totals);
      setPage(d.currentPage);
      setTotalPages(d.totalPages);
    } else toast.error(d.error || "Could not load settlements");
    setLoading(false);
  }, []);

  const loadDrivers = useCallback(async (p, term) => {
    setDrvLoading(true);
    const d = await getDriverBalances({ page: p, limit: 10, search: term });
    if (d.success) {
      setDrvRows(d.drivers);
      setDrvPage(d.currentPage);
      setDrvPages(d.totalPages);
    } else toast.error(d.error || "Could not load driver balances");
    setDrvLoading(false);
  }, []);

  const loadGst = useCallback(async () => {
    setGstLoading(true);
    const d = await getGstReport(gstFrom, gstTo);
    if (d.success) setGst(d);
    else toast.error(d.error || "Could not load GST report");
    setGstLoading(false);
  }, [gstFrom, gstTo]);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);
  useEffect(() => {
    loadSettlements(1, {
      weekStart: weekStart || undefined,
      status: status || undefined,
      search: search || undefined,
    });
  }, [weekStart, status, search, loadSettlements]);
  useEffect(() => {
    if (tab === "drivers") loadDrivers(1, drvSearch);
  }, [tab, drvSearch, loadDrivers]);
  useEffect(() => {
    if (tab === "gst") loadGst();
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshAll = () => {
    loadOverview();
    loadSettlements(page, {
      weekStart: weekStart || undefined,
      status: status || undefined,
      search: search || undefined,
    });
  };

  // ── Actions ────────────────────────────────────────────────────────────────
  const openDetail = async (row) => {
    setDetail({ settlement: row, earnings: [] });
    setDetailLoading(true);
    const d = await getSettlementDetail(row._id);
    if (d.success)
      setDetail({
        settlement: {
          ...d.settlement,
          bankDetails: d.settlement.bankDetails || row.bankDetails,
        },
        earnings: d.earnings,
      });
    else toast.error(d.error || "Could not load details");
    setDetailLoading(false);
  };

  const openPay = (row) => {
    setPayTarget(row);
    setPayForm({
      paymentMode:
        (row.bankDetails?.upiId || row.payoutDetails?.upiId) &&
        !(row.bankDetails?.accountNumber || row.payoutDetails?.accountNumber)
          ? "UPI"
          : "BANK_TRANSFER",
      utr: "",
      remarks: "",
    });
  };

  const submitPay = async () => {
    if (payForm.paymentMode !== "CASH" && !payForm.utr.trim()) {
      toast.error("UTR / reference number is required");
      return;
    }
    setSaving(true);
    const d = await updateSettlementStatus(payTarget._id, {
      status: "PAID",
      ...payForm,
    });
    setSaving(false);
    if (d.success) {
      toast.success(
        `Marked paid — ${payTarget.driverName || payTarget.driverId}`,
      );
      setPayTarget(null);
      setDetail(null);
      refreshAll();
    } else toast.error(d.error || "Could not update");
  };

  const quickStatus = async (row, newStatus) => {
    const d = await updateSettlementStatus(row._id, { status: newStatus });
    if (d.success) {
      toast.success(`Status → ${STATUS_STYLE[newStatus].label}`);
      setDetail((prev) =>
        prev ? { ...prev, settlement: d.settlement } : prev,
      );
      refreshAll();
    } else toast.error(d.error || "Could not update");
  };

  const submitGenerate = async () => {
    setGenerating(true);
    const d = await generateSettlements(genWeek);
    setGenerating(false);
    if (d.success) {
      const s = d.summary;
      toast.success(
        `${s.period}: ${s.created} created, ${s.skippedExisting} already existed, ${s.rolledOver} rolled over`,
      );
      setShowGenerate(false);
      refreshAll();
    } else toast.error(d.error || "Could not generate");
  };

  const exportCsv = async () => {
    const r = await downloadSettlementsCsv({
      weekStart: weekStart || undefined,
      status: status || undefined,
      search: search || undefined,
    });
    if (!r.success) toast.error(r.error || "Export failed");
  };

  // ═══════════════════════════ RENDER ═══════════════════════════
  const tabBtn = (key, label) => (
    <button
      onClick={() => setTab(key)}
      style={{
        padding: "9px 18px",
        border: "none",
        cursor: "pointer",
        fontSize: 14,
        fontWeight: 600,
        background: "none",
        color: tab === key ? BRAND.primaryDark : "#6B7280",
        borderBottom:
          tab === key ? `3px solid ${BRAND.primary}` : "3px solid transparent",
      }}
    >
      {label}
    </button>
  );

  return (
    <div style={{ padding: 20 }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: 12,
          marginBottom: 16,
        }}
      >
        <div>
          <h2 style={{ margin: 0 }}>Driver Settlements</h2>
          <div style={{ fontSize: 13, color: "#6B7280", marginTop: 4 }}>
            Weekly (Mon–Sun) wallet ride payouts · next pay date{" "}
            <b>{overview?.nextPayDate || "—"}</b>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button style={S.btnGhost} onClick={refreshAll}>
            ↻ Refresh
          </button>
          <button style={S.btn} onClick={() => setShowGenerate(true)}>
            Generate settlements
          </button>
        </div>
      </div>

      {/* Overview */}
      <div
        style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 18 }}
      >
        <StatCard
          label="Passenger wallet balance"
          value={inr(overview?.walletLiability)}
          hint={`${overview?.walletCount ?? 0} wallets · unspent, not our income`}
          accent="#6366F1"
        />
        <StatCard
          label="Accruing (this week)"
          value={inr(overview?.driverAccrued)}
          hint={`Settles on ${overview?.nextPayDate || "—"}`}
          accent="#F59E0B"
        />
        <StatCard
          label="Awaiting payment"
          value={inr(overview?.driverAwaiting)}
          hint={`${overview?.settlementsAwaiting ?? 0} settlements to pay`}
          accent="#EF4444"
        />
        <StatCard
          label="Paid to drivers"
          value={inr(overview?.driverPaid)}
          hint={`${overview?.settlementsPaid ?? 0} settlements`}
        />
        <StatCard
          label="GST this month"
          value={inr(overview?.gstThisMonth)}
          hint="Ride GST payable to Govt (Sec 9(5))"
          accent="#0EA5E9"
        />
      </div>

      {/* Tabs */}
      <div style={{ borderBottom: "1px solid #E5E7EB", marginBottom: 16 }}>
        {tabBtn("settlements", "Weekly settlements")}
        {tabBtn("drivers", "Driver-wise balance")}
        {tabBtn("gst", "GST report")}
      </div>

      {/* ───────── Weekly settlements ───────── */}
      {tab === "settlements" && (
        <div style={S.card}>
          <div
            style={{
              display: "flex",
              gap: 10,
              flexWrap: "wrap",
              alignItems: "center",
              marginBottom: 12,
            }}
          >
            <label style={{ fontSize: 12, color: "#6B7280" }}>Week of</label>
            <input
              type="date"
              style={S.input}
              value={weekStart}
              onChange={(e) => setWeekStart(e.target.value)}
            />
            <select
              style={S.input}
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="">All status</option>
              {Object.keys(STATUS_STYLE).map((k) => (
                <option key={k} value={k}>
                  {STATUS_STYLE[k].label}
                </option>
              ))}
            </select>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                setSearch(searchInput.trim());
              }}
              style={{ display: "flex", gap: 6 }}
            >
              <input
                style={{ ...S.input, width: 220 }}
                placeholder="Driver name / phone / ID"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
              <button style={S.btnGhost} type="submit">
                Search
              </button>
              {search && (
                <button
                  style={S.btnGhost}
                  type="button"
                  onClick={() => {
                    setSearchInput("");
                    setSearch("");
                  }}
                >
                  Clear
                </button>
              )}
            </form>
            {weekStart && (
              <button style={S.btnGhost} onClick={() => setWeekStart("")}>
                All weeks
              </button>
            )}
            <div style={{ flex: 1 }} />
            <button style={S.btnGhost} onClick={exportCsv}>
              ⬇ Export CSV
            </button>
          </div>

          {totals && (
            <div
              style={{
                display: "flex",
                gap: 18,
                flexWrap: "wrap",
                fontSize: 13,
                marginBottom: 10,
                color: "#374151",
              }}
            >
              <span>
                Total payable: <b>{inr(totals.netPayable)}</b>
              </span>
              <span>
                Paid:{" "}
                <b style={{ color: BRAND.primaryDark }}>{inr(totals.paid)}</b>
              </span>
              <span>
                Balance to pay:{" "}
                <b style={{ color: "#B42318" }}>{inr(totals.unpaid)}</b>
              </span>
              <span>
                GST in these rides: <b>{inr(totals.gstCollected)}</b>
              </span>
            </div>
          )}

          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  {[
                    "Driver",
                    "Bank details",
                    "Week",
                    "Rides",
                    "Ride earnings",
                    "Referral",
                    "Adjust.",
                    "GST (Govt)",
                    "Net payable",
                    "Pay on",
                    "Status",
                    "",
                  ].map((h) => (
                    <th key={h} style={S.th}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td style={S.td} colSpan={12}>
                      Loading…
                    </td>
                  </tr>
                )}
                {!loading && rows.length === 0 && (
                  <tr>
                    <td
                      style={{
                        ...S.td,
                        textAlign: "center",
                        color: "#9CA3AF",
                        padding: 30,
                      }}
                      colSpan={12}
                    >
                      No settlements found
                    </td>
                  </tr>
                )}
                {!loading &&
                  rows.map((r) => (
                    <tr key={r._id}>
                      <td style={S.td}>
                        <div style={{ fontWeight: 600 }}>
                          {r.driverName || r.driverId}
                        </div>
                        <div style={{ fontSize: 11, color: "#9CA3AF" }}>
                          {r.driverPhone} · {r.driverId}
                        </div>
                      </td>
                      <td style={S.td}>
                        {r.bankDetails?.accountNumber || r.bankDetails?.upiId ? (
                          <div style={{ fontSize: 12, lineHeight: 1.5 }}>
                            <div style={{ fontWeight: 600 }}>
                              {r.bankDetails?.accountHolderName || "—"}
                            </div>
                            <div>A/c: {r.bankDetails?.accountNumber || "—"}</div>
                            <div>
                              IFSC: {r.bankDetails?.ifsc || "—"}
                              {r.bankDetails?.accountType
                                ? ` (${r.bankDetails.accountType})`
                                : ""}
                            </div>
                            <div>UPI: {r.bankDetails?.upiId || "—"}</div>
                          </div>
                        ) : (
                          <span style={{ color: "#DC2626", fontSize: 12 }}>
                            Not added
                          </span>
                        )}
                      </td>
                      <td style={S.td}>{r.periodLabel}</td>
                      <td style={S.td}>{r.rideCount}</td>
                      <td style={S.td}>{inr(r.rideEarnings)}</td>
                      <td style={S.td}>{inr(r.referralIncentives)}</td>
                      <td
                        style={{
                          ...S.td,
                          color: r.adjustments < 0 ? "#B42318" : undefined,
                        }}
                      >
                        {inr(r.adjustments)}
                      </td>
                      <td style={S.td}>{inr(r.gstCollected)}</td>
                      <td style={{ ...S.td, fontWeight: 700 }}>
                        {inr(r.netPayable)}
                      </td>
                      <td style={S.td}>{fmtDate(r.scheduledPayDate)}</td>
                      <td style={S.td}>
                        <StatusBadge status={r.status} />
                      </td>
                      <td style={S.td}>
                        <button
                          style={{ ...S.btnGhost, padding: "5px 10px" }}
                          onClick={() => openDetail(r)}
                        >
                          View
                        </button>{" "}
                        {r.status !== "PAID" && (
                          <button
                            style={{ ...S.btn, padding: "5px 10px" }}
                            onClick={() => openPay(r)}
                          >
                            Pay
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          <Pager
            page={page}
            totalPages={totalPages}
            disabled={loading}
            onChange={(p) =>
              loadSettlements(p, {
                weekStart: weekStart || undefined,
                status: status || undefined,
                search: search || undefined,
              })
            }
          />
        </div>
      )}

      {/* ───────── Driver-wise balance ───────── */}
      {tab === "drivers" && (
        <div style={S.card}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setDrvSearch(drvSearchInput.trim());
            }}
            style={{ display: "flex", gap: 6, marginBottom: 12 }}
          >
            <input
              style={{ ...S.input, width: 240 }}
              placeholder="Driver name / phone / ID"
              value={drvSearchInput}
              onChange={(e) => setDrvSearchInput(e.target.value)}
            />
            <button style={S.btnGhost} type="submit">
              Search
            </button>
            {drvSearch && (
              <button
                style={S.btnGhost}
                type="button"
                onClick={() => {
                  setDrvSearchInput("");
                  setDrvSearch("");
                }}
              >
                Clear
              </button>
            )}
          </form>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  {[
                    "Driver",
                    "Wallet rides",
                    "Accruing (next settlement)",
                    "Awaiting payment",
                    "Paid so far",
                    "Lifetime",
                  ].map((h) => (
                    <th key={h} style={S.th}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {drvLoading && (
                  <tr>
                    <td style={S.td} colSpan={6}>
                      Loading…
                    </td>
                  </tr>
                )}
                {!drvLoading && drvRows.length === 0 && (
                  <tr>
                    <td
                      style={{
                        ...S.td,
                        textAlign: "center",
                        color: "#9CA3AF",
                        padding: 30,
                      }}
                      colSpan={6}
                    >
                      No driver earnings yet
                    </td>
                  </tr>
                )}
                {!drvLoading &&
                  drvRows.map((d) => (
                    <tr key={d.driverId}>
                      <td style={S.td}>
                        <div style={{ fontWeight: 600 }}>
                          {d.name || d.driverId}
                        </div>
                        <div style={{ fontSize: 11, color: "#9CA3AF" }}>
                          {d.phone} · {d.driverId}
                        </div>
                      </td>
                      <td style={S.td}>{d.rideCount}</td>
                      <td
                        style={{ ...S.td, color: "#8A5A00", fontWeight: 600 }}
                      >
                        {inr(d.accruing)}
                      </td>
                      <td
                        style={{ ...S.td, color: "#B42318", fontWeight: 600 }}
                      >
                        {inr(d.awaiting)}
                      </td>
                      <td
                        style={{
                          ...S.td,
                          color: BRAND.primaryDark,
                          fontWeight: 600,
                        }}
                      >
                        {inr(d.paid)}
                      </td>
                      <td style={S.td}>{inr(d.lifetimeEarned)}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          <Pager
            page={drvPage}
            totalPages={drvPages}
            disabled={drvLoading}
            onChange={(p) => loadDrivers(p, drvSearch)}
          />
        </div>
      )}

      {/* ───────── GST report ───────── */}
      {tab === "gst" && (
        <div style={S.card}>
          <div
            style={{
              display: "flex",
              gap: 10,
              alignItems: "center",
              flexWrap: "wrap",
              marginBottom: 12,
            }}
          >
            <label style={{ fontSize: 12, color: "#6B7280" }}>From</label>
            <input
              type="date"
              style={S.input}
              value={gstFrom}
              onChange={(e) => setGstFrom(e.target.value)}
            />
            <label style={{ fontSize: 12, color: "#6B7280" }}>To</label>
            <input
              type="date"
              style={S.input}
              value={gstTo}
              onChange={(e) => setGstTo(e.target.value)}
            />
            <button style={S.btn} onClick={loadGst} disabled={gstLoading}>
              {gstLoading ? "Loading…" : "Run report"}
            </button>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  {[
                    "GST rate",
                    "Rides",
                    "Taxable value",
                    "GST",
                    "CGST",
                    "SGST",
                    "Commission",
                    "Commission GST",
                  ].map((h) => (
                    <th key={h} style={S.th}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(gst?.byRate || []).length === 0 && (
                  <tr>
                    <td
                      style={{
                        ...S.td,
                        textAlign: "center",
                        color: "#9CA3AF",
                        padding: 30,
                      }}
                      colSpan={8}
                    >
                      {gstLoading ? "Loading…" : "No data for this period"}
                    </td>
                  </tr>
                )}
                {(gst?.byRate || []).map((r) => (
                  <tr key={r.gstRate}>
                    <td style={S.td}>{r.gstRate}%</td>
                    <td style={S.td}>{r.rides}</td>
                    <td style={S.td}>{inr(r.taxableValue)}</td>
                    <td style={{ ...S.td, fontWeight: 700 }}>{inr(r.gst)}</td>
                    <td style={S.td}>{inr(r.cgst)}</td>
                    <td style={S.td}>{inr(r.sgst)}</td>
                    <td style={S.td}>{inr(r.commission)}</td>
                    <td style={S.td}>{inr(r.commissionGst)}</td>
                  </tr>
                ))}
                {gst?.totals && (gst.byRate || []).length > 0 && (
                  <tr style={{ background: "#F9FAFB", fontWeight: 700 }}>
                    <td style={S.td}>Total</td>
                    <td style={S.td}>{gst.totals.rides}</td>
                    <td style={S.td}>{inr(gst.totals.taxableValue)}</td>
                    <td style={S.td}>{inr(gst.totals.gst)}</td>
                    <td style={S.td} colSpan={2}></td>
                    <td style={S.td}>{inr(gst.totals.commission)}</td>
                    <td style={S.td}>{inr(gst.totals.commissionGst)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <p style={{ fontSize: 12, color: "#9CA3AF", marginTop: 12 }}>
            {gst?.note ||
              "Ride GST (Sec 9(5)) and commission GST are shown separately. Confirm filing treatment with your CA."}
          </p>
        </div>
      )}

      {/* ───────── Mark paid modal ───────── */}
      {payTarget && (
        <Modal
          title="Mark settlement as paid"
          onClose={() => !saving && setPayTarget(null)}
        >
          <div
            style={{
              background: BRAND.primaryBg,
              border: `1px solid ${BRAND.primaryBorder}`,
              borderRadius: 10,
              padding: 12,
              marginBottom: 14,
              fontSize: 13,
            }}
          >
            <div style={{ fontWeight: 700, fontSize: 15 }}>
              {payTarget.driverName || payTarget.driverId}
            </div>
            <div style={{ color: "#4B5563" }}>{payTarget.periodLabel}</div>
            <div
              style={{
                fontSize: 22,
                fontWeight: 800,
                color: BRAND.primaryDark,
                margin: "6px 0",
              }}
            >
              {inr(payTarget.netPayable)}
            </div>
            <div>
              UPI:{" "}
              <b>
                {payTarget.bankDetails?.upiId ||
                  payTarget.payoutDetails?.upiId ||
                  "—"}
              </b>
            </div>
            <div>
              A/c:{" "}
              <b>
                {payTarget.bankDetails?.accountNumber ||
                  payTarget.payoutDetails?.accountNumber ||
                  "—"}
              </b>{" "}
              · IFSC:{" "}
              <b>
                {payTarget.bankDetails?.ifsc ||
                  payTarget.payoutDetails?.ifsc ||
                  "—"}
              </b>
              {payTarget.bankDetails?.accountType
                ? ` (${payTarget.bankDetails.accountType})`
                : ""}
            </div>
            
            <div>
              Name:{" "}
              <b>
                {payTarget.bankDetails?.accountHolderName ||
                  payTarget.payoutDetails?.accountHolder ||
                  "—"}
              </b>
            </div>
          </div>
          <label style={{ fontSize: 12, color: "#6B7280" }}>Payment mode</label>
          <select
            style={{ ...S.input, width: "100%", marginBottom: 10 }}
            value={payForm.paymentMode}
            onChange={(e) =>
              setPayForm({ ...payForm, paymentMode: e.target.value })
            }
          >
            <option value="UPI">UPI</option>
            <option value="BANK_TRANSFER">Bank transfer (IMPS/NEFT)</option>
            <option value="CASH">Cash</option>
            <option value="OTHER">Other</option>
          </select>
          <label style={{ fontSize: 12, color: "#6B7280" }}>
            UTR / reference no.{" "}
            {payForm.paymentMode === "CASH" ? "(optional)" : "*"}
          </label>
          <input
            style={{
              ...S.input,
              width: "100%",
              boxSizing: "border-box",
              marginBottom: 10,
            }}
            value={payForm.utr}
            onChange={(e) => setPayForm({ ...payForm, utr: e.target.value })}
            placeholder="e.g. 4256xxxxxxx"
          />
          <label style={{ fontSize: 12, color: "#6B7280" }}>Remarks</label>
          <input
            style={{
              ...S.input,
              width: "100%",
              boxSizing: "border-box",
              marginBottom: 16,
            }}
            value={payForm.remarks}
            onChange={(e) =>
              setPayForm({ ...payForm, remarks: e.target.value })
            }
          />
          <div style={{ fontSize: 12, color: "#B42318", marginBottom: 12 }}>
            Paid nu mark pannina apram maathave mudiyaadhu.
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <button
              style={S.btnGhost}
              disabled={saving}
              onClick={() => setPayTarget(null)}
            >
              Cancel
            </button>
            <button style={S.btn} disabled={saving} onClick={submitPay}>
              {saving ? "Saving…" : "Confirm paid"}
            </button>
          </div>
        </Modal>
      )}

      {/* ───────── Detail modal ───────── */}
      {detail && !payTarget && (
        <Modal
          title={`Settlement ${detail.settlement.settlementNo}`}
          onClose={() => setDetail(null)}
          width={760}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 10,
              marginBottom: 12,
            }}
          >
            <div>
              <div style={{ fontWeight: 700, fontSize: 16 }}>
                {detail.settlement.driverName || detail.settlement.driverId}
              </div>
              <div style={{ fontSize: 12, color: "#6B7280" }}>
                {detail.settlement.periodLabel} · pay on{" "}
                {fmtDate(detail.settlement.scheduledPayDate)}
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <StatusBadge status={detail.settlement.status} />
              <div style={{ fontSize: 22, fontWeight: 800, marginTop: 4 }}>
                {inr(detail.settlement.netPayable)}
              </div>
            </div>
          </div>

          {detail.settlement.status === "PAID" && (
            <div
              style={{
                background: BRAND.primaryBg,
                borderRadius: 8,
                padding: 10,
                fontSize: 13,
                marginBottom: 12,
              }}
            >
              Paid {fmtDate(detail.settlement.paidAt)} via{" "}
              <b>{detail.settlement.paymentMode}</b>
              {detail.settlement.utr && (
                <>
                  {" "}
                  · UTR <b>{detail.settlement.utr}</b>
                </>
              )}{" "}
              · by {detail.settlement.paidBy}
            </div>
          )}

          {/* ✅ Bank details */}
          <div
            style={{
              background: "#F9FAFB",
              border: "1px solid #E5E7EB",
              borderRadius: 8,
              padding: 10,
              fontSize: 13,
              marginBottom: 12,
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: 6,
            }}
          >
            <div>
              Holder:{" "}
              <b>{detail.settlement.bankDetails?.accountHolderName || "—"}</b>
            </div>
            <div>
              A/c No:{" "}
              <b>{detail.settlement.bankDetails?.accountNumber || "—"}</b>
            </div>
            <div>
              IFSC: <b>{detail.settlement.bankDetails?.ifsc || "—"}</b>
            </div>
            <div>
              Type: <b>{detail.settlement.bankDetails?.accountType || "—"}</b>
            </div>
            <div>
              UPI: <b>{detail.settlement.bankDetails?.upiId || "—"}</b>
            </div>
            <div>
              Verified:{" "}
              <b>
                {detail.settlement.bankDetails?.verifiedAt
                  ? fmtDate(detail.settlement.bankDetails.verifiedAt)
                  : "—"}
              </b>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
              fontSize: 12,
              marginBottom: 12,
            }}
          >
            {[
              ["Base fare", detail.settlement.baseFare],
              ["GST (Govt)", detail.settlement.gstCollected],
              ["Commission", detail.settlement.commission],
              ["Commission GST", detail.settlement.commissionGst],
              ["Referral", detail.settlement.referralIncentives],
              ["Adjustments", detail.settlement.adjustments],
            ].map(([k, v]) => (
              <div
                key={k}
                style={{
                  background: "#F3F4F6",
                  borderRadius: 8,
                  padding: "6px 10px",
                }}
              >
                {k}: <b>{inr(v)}</b>
              </div>
            ))}
          </div>

          <div
            style={{
              maxHeight: 260,
              overflow: "auto",
              border: "1px solid #E5E7EB",
              borderRadius: 8,
            }}
          >
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  {[
                    "Date",
                    "Type",
                    "Ride / ref",
                    "Fare",
                    "GST",
                    "Commission",
                    "Net",
                  ].map((h) => (
                    <th key={h} style={S.th}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {detailLoading && (
                  <tr>
                    <td style={S.td} colSpan={7}>
                      Loading…
                    </td>
                  </tr>
                )}
                {!detailLoading &&
                  detail.earnings.map((e) => (
                    <tr key={e._id}>
                      <td style={S.td}>{fmtDate(e.earnedAt)}</td>
                      <td style={S.td}>{e.type.replace("_", " ")}</td>
                      <td style={S.td}>{e.rideId || e.sourceKey}</td>
                      <td style={S.td}>{inr(e.baseFare)}</td>
                      <td style={S.td}>{inr(e.gstAmount)}</td>
                      <td style={S.td}>
                        {inr(e.commissionAmount + e.commissionGst)}
                      </td>
                      <td
                        style={{
                          ...S.td,
                          fontWeight: 600,
                          color: e.netAmount < 0 ? "#B42318" : undefined,
                        }}
                      >
                        {inr(e.netAmount)}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          {detail.settlement.status !== "PAID" && (
            <div
              style={{
                display: "flex",
                gap: 8,
                justifyContent: "flex-end",
                marginTop: 14,
                flexWrap: "wrap",
              }}
            >
              <button
                style={S.btnGhost}
                onClick={() => quickStatus(detail.settlement, "PROCESSING")}
              >
                Mark processing
              </button>
              <button
                style={S.btnGhost}
                onClick={() => quickStatus(detail.settlement, "ON_HOLD")}
              >
                Hold
              </button>
              <button
                style={S.btnGhost}
                onClick={() => quickStatus(detail.settlement, "FAILED")}
              >
                Mark failed
              </button>
              <button style={S.btn} onClick={() => openPay(detail.settlement)}>
                Mark paid
              </button>
            </div>
          )}
        </Modal>
      )}

      {/* ───────── Generate modal ───────── */}
      {showGenerate && (
        <Modal
          title="Generate weekly settlements"
          onClose={() => !generating && setShowGenerate(false)}
          width={420}
        >
          <p style={{ fontSize: 13, color: "#4B5563", marginTop: 0 }}>
            Cron automatic-a every Monday 00:05 IST run aagum. Miss aanadhu /
            manual-a venum na inga run pannalam. Already generate aana
            driver-week thirumba create aagaadhu.
          </p>
          <label style={{ fontSize: 12, color: "#6B7280" }}>
            Any date in the week (Mon–Sun)
          </label>
          <input
            type="date"
            style={{
              ...S.input,
              width: "100%",
              boxSizing: "border-box",
              margin: "6px 0 16px",
            }}
            value={genWeek}
            onChange={(e) => setGenWeek(e.target.value)}
          />
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <button
              style={S.btnGhost}
              disabled={generating}
              onClick={() => setShowGenerate(false)}
            >
              Cancel
            </button>
            <button
              style={S.btn}
              disabled={generating || !genWeek}
              onClick={submitGenerate}
            >
              {generating ? "Running…" : "Generate"}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}